import React, { useMemo } from 'react';
import { format, isToday, isWeekend } from 'date-fns';
import useTranslation from '../../../hooks/useTranslation';
import { toMinutes } from '../../../services/workerAssignmentsService';
import { shortTime } from './AssignmentModal';

/** Hauteur d'une heure, en pixels. Tout le positionnement en decoule. */
const HOUR_PX = 36;

/** En dessous, la note ne tient pas : on garde l'heure, le nom et le projet. */
const NOTE_MIN_HEIGHT = 80;

/**
 * La plage horaire de la grille, DEDUITE de la semaine affichee.
 *
 * Une semaine ordinaire (6h-14h) tient ainsi entierement a l'ecran sans defiler ;
 * une semaine avec des travaux de soir s'etire d'elle-meme. Une heure de marge de
 * chaque cote, pour que rien ne colle au bord.
 *
 * Semaine vide, ou filtre qui ne laisse rien : 7h -> 17h, la journee de chantier
 * par defaut. Bornes dures a 0h et 24h.
 */
const gridRange = (assignments) => {
  const mins = (assignments || [])
    ?.flatMap((a) => [toMinutes(a?.startTime), toMinutes(a?.endTime)])
    ?.filter((m) => m != null);

  if (!mins?.length) return { startHour: 7, endHour: 17 };

  return {
    startHour: Math.max(0, Math.floor(Math.min(...mins) / 60) - 1),
    endHour: Math.min(24, Math.ceil(Math.max(...mins) / 60) + 1),
  };
};

/**
 * Repartit en colonnes les assignations d'UN jour qui se chevauchent.
 *
 * Une meme personne ne peut plus se chevaucher — la contrainte EXCLUDE l'interdit.
 * Mais DEUX personnes le peuvent, et c'est le cas normal : trois gars sur le meme
 * chantier de 7h a 15h.
 *
 * Algorithme volontairement simple : on trie par heure de debut, on groupe ce qui
 * se touche, et dans un groupe de N chacun prend 1/N de la largeur. Un bloc seul
 * prend toute la colonne.
 */
const layoutDay = (items) => {
  const sorted = [...(items || [])]?.sort((a, b) => {
    const d = (toMinutes(a?.startTime) || 0) - (toMinutes(b?.startTime) || 0);
    return d !== 0 ? d : (toMinutes(a?.endTime) || 0) - (toMinutes(b?.endTime) || 0);
  });

  const groups = [];
  let current = [];
  let currentEnd = -1;

  sorted?.forEach((it) => {
    const s = toMinutes(it?.startTime) ?? 0;
    const e = toMinutes(it?.endTime) ?? 0;
    if (current?.length && s < currentEnd) {
      current?.push(it);
      currentEnd = Math.max(currentEnd, e);
    } else {
      if (current?.length) groups?.push(current);
      current = [it];
      currentEnd = e;
    }
  });
  if (current?.length) groups?.push(current);

  return groups?.flatMap((g) => g?.map((item, index) => ({ item, index, total: g?.length })));
};

const WeekView = ({ days, byDate, workerMap, projectMap, colorOf, onSelect, onCreate }) => {
  const { language } = useTranslation();

  const weekAssignments = useMemo(
    () => (days || [])?.flatMap((d) => byDate?.[d?.iso] || []),
    [days, byDate]
  );

  const { startHour, endHour } = useMemo(() => gridRange(weekAssignments), [weekAssignments]);
  const hours = useMemo(
    () => Array.from({ length: endHour - startHour }, (_, i) => startHour + i),
    [startHour, endHour]
  );
  const gridHeight = (endHour - startHour) * HOUR_PX;

  /** Ou cliquer se traduit en heure, arrondie a la demi-heure. */
  const timeAtOffset = (offsetY) => {
    const raw = startHour * 60 + (offsetY / HOUR_PX) * 60;
    const rounded = Math.round(raw / 30) * 30;
    const clamped = Math.min(Math.max(rounded, startHour * 60), endHour * 60 - 30);
    const hh = String(Math.floor(clamped / 60))?.padStart(2, '0');
    const mm = String(clamped % 60)?.padStart(2, '0');
    return `${hh}:${mm}`;
  };

  return (
    <div className="overflow-x-auto pb-1">
      <div className="min-w-[900px]">
        {/* En-tete des colonnes */}
        <div className="flex mb-1.5">
          <div style={{ width: '56px' }} className="flex-shrink-0" />
          {days?.map((d) => {
            const weekend = isWeekend(d?.date);
            const today = isToday(d?.date);
            return (
              <div key={d?.iso} className="flex-1 min-w-0 px-0.5">
                <div className="flex flex-col items-center gap-0.5 py-1">
                  <span className={`text-[10px] font-semibold uppercase tracking-wide ${weekend ? 'text-gray-300' : 'text-gray-500'}`}>
                    {d?.date?.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'short' })}
                  </span>
                  {today ? (
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold">
                      {format(d?.date, 'd')}
                    </span>
                  ) : (
                    <span className={`text-[11px] font-medium ${weekend ? 'text-gray-300' : 'text-gray-600'}`}>
                      {format(d?.date, 'd')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* La grille */}
        <div className="flex border border-gray-200 rounded-lg overflow-hidden bg-white">
          {/* Colonne des heures */}
          <div style={{ width: '56px', height: gridHeight }} className="flex-shrink-0 relative border-r border-gray-200 bg-gray-50/60">
            {hours?.map((h, i) => (
              <div
                key={h}
                className="absolute right-1.5 text-[10px] text-gray-400 tabular-nums -translate-y-1/2"
                style={{ top: i * HOUR_PX }}
              >
                {i === 0 ? '' : `${h}:00`}
              </div>
            ))}
          </div>

          {/* Sept colonnes, toujours les sept — meme vides */}
          {days?.map((d) => {
            const weekend = isWeekend(d?.date);
            const placed = layoutDay(byDate?.[d?.iso] || []);
            return (
              <div
                key={d?.iso}
                className={`flex-1 min-w-0 relative border-r border-gray-100 last:border-r-0 ${weekend ? 'bg-slate-50/60' : ''}`}
                style={{ height: gridHeight }}
                onClick={(e) => {
                  const rect = e?.currentTarget?.getBoundingClientRect();
                  onCreate?.(d?.iso, timeAtOffset(e?.clientY - rect?.top));
                }}
              >
                {/* Traits d'heure */}
                {hours?.map((h, i) => (
                  <div
                    key={h}
                    className="absolute left-0 right-0 border-t border-gray-100 pointer-events-none"
                    style={{ top: i * HOUR_PX }}
                  />
                ))}

                {/* Les blocs */}
                {placed?.map(({ item: a, index, total }) => {
                  const s = toMinutes(a?.startTime);
                  const e = toMinutes(a?.endTime);
                  if (s == null || e == null) return null;

                  const top = ((s - startHour * 60) / 60) * HOUR_PX;
                  const height = Math.max(18, ((e - s) / 60) * HOUR_PX);
                  const c = colorOf(a?.projectId);
                  const p = projectMap?.[a?.projectId];
                  const showNote = height >= NOTE_MIN_HEIGHT && !!a?.note;

                  return (
                    <button
                      key={a?.id}
                      onClick={(ev) => { ev?.stopPropagation(); onSelect?.(a); }}
                      title={a?.note || undefined}
                      className="absolute rounded overflow-hidden text-left flex items-stretch hover:brightness-95 transition-all"
                      style={{
                        top: top + 1,
                        height: height - 2,
                        left: `calc(${(index / total) * 100}% + 2px)`,
                        width: `calc(${100 / total}% - 4px)`,
                        backgroundColor: c?.bg,
                      }}
                    >
                      <span className="w-[3px] flex-shrink-0" style={{ backgroundColor: c?.bar }} />
                      <span className="flex-1 min-w-0 px-1.5 py-1 flex flex-col gap-0.5 overflow-hidden">
                        <span className="text-[10px] font-bold tabular-nums leading-tight" style={{ color: c?.text }}>
                          {shortTime(a?.startTime)} – {shortTime(a?.endTime)}
                        </span>
                        <span className="text-[10px] font-medium truncate leading-tight" style={{ color: c?.text }}>
                          {workerMap?.[a?.workerId]?.full_name || '—'}
                        </span>
                        <span className="text-[9px] truncate leading-tight opacity-80" style={{ color: c?.text }}>
                          {p?.projectNumber ? `${p?.projectNumber} · ${p?.name}` : (p?.name || '')}
                        </span>
                        {/* 🔴 La note est la raison d'etre de cette vue : la grille du
                            mois ne peut pas la montrer. */}
                        {showNote && (
                          <span
                            className="text-[9px] leading-snug opacity-75 mt-0.5 pt-0.5 border-t overflow-hidden"
                            style={{ color: c?.text, borderColor: c?.bar, borderTopWidth: '1px' }}
                          >
                            {a?.note}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WeekView;
